import React from 'react';
import config from 'src/config';
import { Gem } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import UpgradeSubscriptionButton from 'src/shared/components/upgrade-subscription-button/UpgradeSubscriptionButton';
import useProject from 'src/shared/hooks/useProject';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { useSiteSummaryQuery } from 'src/app/projects/views/site-summary/state/api/siteSummaryGraphSlice';
import SiteContext from 'src/app/projects/views/site-summary/components/SiteContext';
import SiteHabitatHealth from 'src/app/projects/views/site-summary/components/SiteHabitatHealth';
import SiteSpeciesRiskIndicators from 'src/app/projects/views/site-summary/components/SiteSpeciesRiskIndicators';
import SiteSpeciesRiskIndicatorsNext from 'src/app/projects/views/site-summary/components/SiteSpeciesRiskIndicatorsNext';
import storageService from 'src/services/storage-service';
import { WELCOME_DIALOG_DISMISSED_STORAGE_KEY } from 'src/shared/constants/storageKeys';
import SiteSpeciesHealthIndicators from './components/SiteSpeciesHealthIndicators';
import WelcomeDialog from './components/WelcomeDialog';
import useSiteSummaryAnalytics from './hooks/useSiteSummaryAnalytics';
import styles from './SiteSummary.module.scss';

const SiteSummary = () => {
    const [isWelcomeDialogOpen, setIsWelcomeDialogOpen] = React.useState(false);
    const { isStarterProject } = useProject();
    const [searchParams] = useSearchParams();
    const projectId = searchParams.get('projectId') || '';
    const { currentCustomerId } = useCustomer();
    const { trackUpgradeSubscriptionClick } = useSiteSummaryAnalytics();

    React.useEffect(() => {
        const isDismissed = Boolean(storageService.get(WELCOME_DIALOG_DISMISSED_STORAGE_KEY));
        setIsWelcomeDialogOpen(!isDismissed);
    }, []);

    const closeWelcomeDialog = React.useCallback(() => {
        storageService.save(WELCOME_DIALOG_DISMISSED_STORAGE_KEY, true);
        setIsWelcomeDialogOpen(false);
    }, []);

    const onWelcomeDialogOpenChange = React.useCallback(
        (open: boolean) => {
            if (!open) {
                closeWelcomeDialog();
                return;
            }

            setIsWelcomeDialogOpen(true);
        },
        [closeWelcomeDialog]
    );

    const { currentData, isFetching } = useSiteSummaryQuery(
        { customerId: currentCustomerId || '', siteId: projectId },
        { skip: !currentCustomerId || !projectId }
    );

    if (isFetching) {
        return <Loader />;
    }
    const isSiteMetricsAvailable = Boolean(currentData?.metrics?.length);

    const pageTitle = currentData?.name || 'Site Summary';
    const pageSubtitle = pageTitle === 'Site Summary' ? '' : 'Site Summary';

    const mainContent = (
        <div className={`flex flex-col gap-10 mb-10 ${styles.mainContainer}`}>
            <SiteContext />
            {currentData && (
                <>
                    {isSiteMetricsAvailable && <SiteHabitatHealth site={currentData} />}
                    {isSiteMetricsAvailable && <SiteSpeciesRiskIndicators site={currentData} />}
                </>
            )}
            {/* Not gated on isSiteMetricsAvailable: grid species do not depend on
                habitat metrics, and gating would hide them on the sites where they
                are the only species figures available. Hides itself for external
                users. */}
            <SiteSpeciesRiskIndicatorsNext siteId={projectId} />
            <SiteSpeciesHealthIndicators />
        </div>
    );

    const topRightSection = isStarterProject ? (
        <div className='flex items-center justify-between bg-white rounded-lg px-5 py-3 shadow-portfolio-summary-card'>
            <div className='flex gap-3 text-primary text-sm w-[300px] items-center'>
                <Gem className='text-primary' />
                Advanced insights available
            </div>
            <UpgradeSubscriptionButton
                link={config.bookDemoCallFormUrl}
                label='Subscribe to access'
                onClick={trackUpgradeSubscriptionClick}
            />
        </div>
    ) : null;

    return (
        <>
            <StandardAppLayout
                title={pageTitle}
                subTitle={pageSubtitle}
                mainContent={mainContent}
                lozenges={topRightSection}
                classNames={{
                    otherActions: 'p-0 justify-normal',
                }}
            />
            <WelcomeDialog open={isWelcomeDialogOpen} onOpenChange={onWelcomeDialogOpenChange} onGetStarted={closeWelcomeDialog} />
        </>
    );
};

export default SiteSummary;
